import type { Editor as EditorType } from 'hugerte';

/**
 * hugerte's silver theme marks every toolbar *group* (`.tox-toolbar__group`) with
 * `role="toolbar"` and gives none of them an accessible name, while the container
 * that actually wraps them (`.tox-toolbar__primary`) is only a `role="group"`. The
 * result is several unnamed toolbars: when focus enters one, NVDA announces just
 * "Toolbar toolbar" (the localized role name, twice) instead of the focused button
 * (Bold, Italic, …).
 *
 * We reshape this into the canonical WAI-ARIA toolbar pattern — a single named
 * `role="toolbar"` on the primary container, with its inner groups demoted to
 * `role="group"`. hugerte's keyboard navigation keys off the `.tox-toolbar__group`
 * class (not the role), so arrow/Alt+F10 navigation is unaffected; focus still
 * lands on the buttons, which carry their own aria-labels.
 *
 * hugerte rebuilds the toolbar group nodes whenever the editor reflows (the default
 * `toolbar_mode: 'floating'` re-runs its group layout on resize / overflow), and each
 * rebuilt group is re-created with `role="toolbar"` restored — so a one-time pass at
 * init silently regresses. We therefore re-apply on every toolbar mutation via a
 * MutationObserver, disconnected when the editor is removed to avoid leaking across
 * the dark-mode remount.
 *
 * `toolbarAriaLabel` is a Corgi-i18n translated string supplied by the caller.
 */
export const fixToolbarAria = (editor: EditorType, toolbarAriaLabel: string): void => {
	const container = editor.getContainer();

	const normalize = (): void => {
		const primary =
			container.querySelector<HTMLElement>('.tox-toolbar__primary') ??
			container.querySelector<HTMLElement>('.tox-toolbar');
		if (primary) {
			primary.setAttribute('role', 'toolbar');
			primary.setAttribute('aria-label', toolbarAriaLabel);
		}

		for (const group of container.querySelectorAll<HTMLElement>('.tox-toolbar__group[role="toolbar"]')) {
			group.setAttribute('role', 'group');
		}
	};

	normalize();

	// Only childList/subtree — never attributes, so our own setAttribute calls don't
	// re-trigger the observer. Content edits live in a separate iframe document and
	// don't reach this observer, so it only wakes on actual toolbar rebuilds.
	const observer = new MutationObserver(normalize);
	observer.observe(container, { childList: true, subtree: true });
	editor.on('remove', () => observer.disconnect());
};
